import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Trans } from "app/utils";
import { NumberField, SelectField } from "@culturehq/components";
import { debounce } from "lodash";
import general from "constants/general";
import { ColorResult } from "react-color";
import { SettingsProps, Subtype } from "./AnimationTypes";
import { ANIMATION_TIME } from "./useAnimation";
import { ANIMATION_CATEGORY, ANIMATIONS, DEFAULT_ANIMATION_SETTINGS } from "../../utils/constants";

import styles from "./animation.module.scss";
import ColorPicker from "../../common/ColorPicker";
import { getHexFromRgbObject, getHslFromRgba, getRgbObjFromString } from "../../utils";

const AnimationSettings = ({ type, selectedAnimation, order, onChange }: SettingsProps) => {
  const subtypes = DEFAULT_ANIMATION_SETTINGS[type] || {};
  const subtypeKeys = Object.keys(subtypes) as Subtype[];

  const selectedSubtype = useMemo(() => {
    const ignoreSubtypeProps = ["duration", "time", "color"];
    const subtypeFound = [...subtypeKeys]
      .sort((a, b) => Object.keys(b).length - Object.keys(a).length)
      .find(key => {
        const dismatch = !!Object.entries(subtypes[key])
          .filter(([prop]) => !ignoreSubtypeProps.includes(prop))
          .find(([prop, value]) => value !== selectedAnimation[prop]);

        return !dismatch;
      });
    return subtypeFound || subtypeKeys[0];
  }, [selectedAnimation, subtypeKeys, subtypes]);

  const defaultAnimationSettings = DEFAULT_ANIMATION_SETTINGS[type]?.[selectedSubtype] || {};

  const [subtype, setSubtype] = useState<Subtype>(selectedSubtype as Subtype);
  const [duration, setDuration] = useState<string>(
    selectedAnimation.duration || defaultAnimationSettings.duration || ANIMATION_TIME
  );

  useEffect(() => setSubtype(selectedSubtype), [selectedSubtype]);
  useEffect(() => setDuration(
    selectedAnimation.duration || defaultAnimationSettings.duration || ANIMATION_TIME
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [selectedAnimation]);

  const rgbaObj = getRgbObjFromString(selectedAnimation.color);
  const [color, setColor] = useState<ColorResult>(selectedAnimation.color
    ? { hex: getHexFromRgbObject(rgbaObj),
      rgb: rgbaObj,
      hsl: getHslFromRgba(rgbaObj)
    } : general.DEFAULT_COLOR);

  const debouncedOnChange = useCallback(debounce(settings => {
    setColor(prev => {
      onChange({ ...settings, color: prev });
      return prev;
    });
  }, 500), [onChange]);

  const handleSetting = (setting: string) => (value: any) => {
    if (setting === "subtype") {
      setSubtype(value);
      debouncedOnChange({ subtype: value, duration: Number(duration) });
    }
    if (setting === "duration") {
      setDuration(value);
      debouncedOnChange({ subtype, duration: Number(value) });
    }
    if (setting === "color") {
      setColor(value);
      debouncedOnChange({ subtype, duration: Number(duration), color });
    }
  };

  const subtypeOptions = useMemo(
    () => subtypeKeys.map(subtype => ({ value: subtype, label: subtype })),
    [subtypeKeys]
  );

  const subtypeLabel = ANIMATION_CATEGORY[type] || "Direction";
  const isColorWipe = type === ANIMATIONS.COLOR_WIPE;
  const colorWipeClass = isColorWipe ? styles.ColorWipe : "";

  return (
    <div className={`${styles.Settings} ${colorWipeClass}`} style={{ order }}>
      <div className={styles.WipeRow}>
        <SelectField
          name="subtype"
          value={subtype}
          options={subtypeOptions}
          onChange={handleSetting("subtype")}
        >
          <Trans>{subtypeLabel}</Trans>
        </SelectField>

        <NumberField
          className={styles.Duration}
          name="duration"
          value={duration}
          onChange={handleSetting("duration")}
        >
          <Trans>Duration</Trans>
        </NumberField>
      </div>
      {isColorWipe
      && (
        <label className="chq-ffd animation_Color">
          <span className="chq-ffd--lb">Color</span>
          <ColorPicker initialColor={color} onChangeColor={handleSetting("color")} />
        </label>
      ) }
    </div>
  );
};

export default AnimationSettings;
